/**
 * O Gerador de Relatórios — migração das quatro telas `GeradorDeRelatorios*.aspx`.
 *
 * Os nomes casam com os DTOs do backend (`GeradorRelatoriosModels.cs`). Ver
 * `docs/gerador-relatorios/README.md`.
 */

/** Qual dos dois geradores. Define o catálogo e a raiz da consulta. */
export enum eTipoGerador {
  Estudantes = 0,
  Funcionarios = 1,
}

/**
 * Até onde o relatório enxerga.
 *
 * **Vem da ROTA, não de um campo da tela.** As quatro rotas existem porque são quatro permissões:
 * a rede precisa poder liberar a visão da rede a quem ela decidir, sem liberar a da escola junto.
 */
export enum eAbrangenciaGerador {
  Escola = 0,
  Rede = 1,
}

/** Um campo do catálogo. */
export class CampoDoCatalogoModel {
  chave = '';
  rotulo = '';
  secao = '';
  grupo = '';
  tipo = '';

  /** Tem rótulo conhecido? A tela usa para recolher "Outros campos". */
  curado = false;
}

/** Uma seção dentro de um grupo (Identificação, Documentos…). */
export class SecaoDoCatalogoModel {
  nome = '';
  campos: CampoDoCatalogoModel[] = [];
}

/** Um grupo do catálogo (Estudante, Turma, Responsável…). */
export class GrupoDoCatalogoModel {
  nome = '';
  secoes: SecaoDoCatalogoModel[] = [];
}

/** O catálogo inteiro, já agrupado pelo servidor. */
export class CatalogoModel {
  grupos: GrupoDoCatalogoModel[] = [];
}

/** O que a tela manda para executar (prévia ou emissão). */
export class ExecutarGeradorInput {
  tipo: eTipoGerador = eTipoGerador.Estudantes;
  abrangencia: eAbrangenciaGerador = eAbrangenciaGerador.Escola;

  /** As colunas, NA ORDEM em que saem. */
  campos: string[] = [];

  codigoEscola = 0;
  ano = 0;
  codigoTurma = 0;
  ordenacao = '';
  recorte = '';

  /** Zero traz tudo (até o teto do servidor); maior que zero é prévia. */
  limite = 0;
}

/** Uma coluna do resultado. */
export class ColunaDoResultadoModel {
  chave = '';
  rotulo = '';
  tipo = '';
}

/**
 * O resultado.
 *
 * As linhas chegam com os valores **já em texto e já traduzidos** (sexo, Sim/Não, datas). A
 * tradução é do servidor de propósito: fazê-la aqui duplicaria a regra em dois lugares, e o Excel
 * — que sai deste lado — ficaria com os códigos.
 */
export class ResultadoGeradorModel {
  colunas: ColunaDoResultadoModel[] = [];
  linhas: Record<string, string>[] = [];
  total = 0;

  /** O servidor cortou o resultado. A tela precisa dizer isso. */
  truncado = false;
}

/** Um modelo salvo — a lista da esquerda na tela do legado. */
export class ModeloDoGeradorModel {
  codigo: number | null = null;
  tipo: eTipoGerador = eTipoGerador.Estudantes;
  descricao = '';
  campos: string[] = [];
  excel = false;
  todasAsEscolas = false;
  codigoEscola = 0;

  /** Veio do legado e ainda está no formato antigo. Vira formato novo ao salvar. */
  formatoLegado = false;
}

/** O que vai em `filtrosEspecificos` na emissão do `REL_FER_GERADOR_RELATORIOS`. */
export class FiltroGeradorRelatorioModel {
  tipo: eTipoGerador = eTipoGerador.Estudantes;
  abrangencia: eAbrangenciaGerador = eAbrangenciaGerador.Escola;
  campos: string[] = [];
  codigoTurma = 0;
  ordenacao = '';
  recorte = '';

  /** O nome do modelo salvo. Vira o título da folha e o nome do PDF. */
  titulo = '';
}

/**
 * Os recortes de situação, iguais aos do legado.
 *
 * O legado tinha 24 opções, muitas delas sem uso e várias que eram a mesma coisa com outro nome.
 * Ficaram as que a secretaria de fato usa; as demais se obtêm escolhendo a coluna "Situação" e
 * filtrando no Excel, que é mais direto do que caçar o rádio certo numa lista de 24.
 */
export const RECORTES_GERADOR = [
  { valor: '', texto: 'Todos' },
  { valor: 'enturmados', texto: 'Enturmados' },
  { valor: 'naoenturmados', texto: 'Matriculados e não enturmados' },
  { valor: 'matriculados', texto: 'Matriculados (ativos)' },
  { valor: 'transferidos', texto: 'Transferidos' },
  { valor: 'evadidos', texto: 'Evadidos' },
  { valor: 'aprovados', texto: 'Aprovados' },
  { valor: 'reprovados', texto: 'Reprovados' },
];

/** O máximo de colunas que o servidor aceita — espelha `ExecutarGeradorUseCase`. */
export const MAXIMO_DE_COLUNAS = 60;

/**
 * Acima disso a folha deixa de servir e o relatório só faz sentido em Excel.
 * Era o que o legado fazia, travando a caixa "Gerar Excel" a partir de sete colunas.
 */
export const COLUNAS_ATE_CABER_NA_FOLHA = 12;
